import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { api } from "@/lib/api";
import { sellerNav, adminNav, buyerNav } from "@/lib/formSpecs";
import { Layout } from "@/components/Layout";

import Storefront from "@/pages/Storefront";
import ProductDetail from "@/pages/ProductDetail";
import Auth from "@/pages/Auth";
import Overview from "@/pages/Overview";
import Profile from "@/pages/Profile";
import Marketplace from "@/pages/Marketplace";
import Documents from "@/pages/Documents";
import FormBuilder from "@/pages/FormBuilder";
import Quotes from "@/pages/Quotes";
import MyOrders from "@/pages/MyOrders";
import Verify from "@/pages/Verify";
import PublicVerify from "@/pages/PublicVerify";
import AdminRouter from "@/pages/Admin";
import AboutUs from "@/pages/AboutUs";
import ContactUs from "@/pages/ContactUs";
import AuthorisedUsers from "@/pages/AuthorisedUsers";
import SellerProfile from "@/pages/SellerProfile";
import BuyerDashboard from "@/pages/BuyerDashboard";
import GIInformation from "@/pages/GIInformation";
import ReturnPolicy from "@/pages/ReturnPolicy";
import AuthCallback from "@/pages/AuthCallback";
import { ChatWidget } from "@/components/ChatWidget";

import "@/App.css";
import "@/Verify.css";
import "@/Storefront.css";

function Shell() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const t = localStorage.getItem("tv_token");
    if (!t) { setLoading(false); return; }
    api.get("/auth/me")
      .then(r => setUser(r.data))
      .catch(() => localStorage.removeItem("tv_token"))
      .finally(() => setLoading(false));
  }, []);

  const path = location.pathname;

  // Google OAuth callback — must handle BEFORE ProtectedRoute / loading check to avoid race
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (path === "/auth/callback" || (location.hash || "").includes("session_id=")) {
    return <AuthCallback onAuth={setUser} />;
  }

  // Public routes (no auth required)
  if (path.startsWith("/verify/")) return <PublicVerify id={path.split("/")[2]} />;
  if (path.startsWith("/product/")) return (
    <><Routes><Route path="/product/:id" element={<ProductDetail />} /></Routes><ChatWidget /></>
  );
  if (path.startsWith("/seller/")) return (
    <><Routes><Route path="/seller/:id" element={<SellerProfile />} /></Routes><ChatWidget /></>
  );
  if (path === "/about") return (<><AboutUs /><ChatWidget /></>);
  if (path === "/contact") return (<><ContactUs /><ChatWidget /></>);
  if (path === "/gi-information") return (<><GIInformation /><ChatWidget /></>);
  if (path === "/return-policy") return (<><ReturnPolicy /><ChatWidget /></>);
  if (path === "/authorised-users") return (<><AuthorisedUsers /><ChatWidget /></>);

  if (loading) return null;

  if (!user && path === "/login") return (<><Auth onAuth={setUser} /><ChatWidget /></>);
  if (!user) return (<><Storefront /><ChatWidget /></>);

  if (user.role === "admin") {
    return (
      <Layout user={user} setUser={setUser} nav={adminNav}>
        <Routes>
          <Route path="/admin/*" element={<AdminRouter />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </Layout>
    );
  }

  if (user.role === "buyer") {
    return (
      <Layout user={user} setUser={setUser} nav={buyerNav}>
        <Routes>
          <Route path="/orders" element={<BuyerDashboard />} />
          <Route path="*" element={<Navigate to="/orders" replace />} />
        </Routes>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser} nav={sellerNav}>
      <Routes>
        <Route path="/" element={<Overview user={user} />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/documents/:formType" element={<FormBuilder />} />
        <Route path="/orders" element={<MyOrders />} />
        <Route path="/quotes" element={<Quotes />} />
        <Route path="/verify" element={<Verify user={user} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default function Root() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
      <Shell />
    </BrowserRouter>
  );
}
